// User detail (SM-860): one broker account on its own page,
// /users/{broker_account_id}?node=&currency=&tab=, opened in a new tab
// from Users and from the Trading pages' account links, so the list a
// user came from keeps its filters, page and scroll. node and currency
// are always in the link: a new tab boots the shell from the URL.
//
// The account, its pause controls (ACCOUNT_PAUSE, only on accounts the
// selected node services DIRECTLY and only when the caller's membership
// sits on that node), a tab strip (?tab=, SM-828) — active orders, active
// positions, order history, trade history, then SM-822's login log and
// order log — and its History.
//
//   GET  /api/broker/{id}/accounts/{aid}                 BrokerAccountItem
//   GET  /api/broker/{id}/accounts/{aid}/events          BrokerAccountEventListResponse
//   GET  /api/broker/{id}/accounts/{aid}/orders?currency=&offset=&limit=          OrderListResponse
//   GET  /api/broker/{id}/accounts/{aid}/positions?currency=&status=OPEN,REDEEMABLE&offset=&limit=
//   GET  /api/broker/{id}/accounts/{aid}/orders/history?currency=&offset=&limit=  OrderListResponse
//   GET  /api/broker/{id}/accounts/{aid}/trades?currency=&offset=&limit=          SettlementTradeListResponse
//        (the account in the PATH: api-gateway strips broker_account_id from every /api/* query and body, SM-693)
//   GET  /api/broker/{id}/accounts/{aid}/login-log?offset=&limit=  BrokerAccountLoginListResponse (SM-822)
//   GET  /api/broker/{id}/accounts/{aid}/order-log?cursor=&limit=  OrderClientLogListResponse (SM-822, keyset)
//   POST /api/broker/{id}/accounts/{aid}/suspend|resume  BrokerAccountTransitionRequest
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCan, useScope, useShellSearch } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { CursorPager, EmptyPage, KeyValues, Pager, Time } from "@/components/ui/Common";
import { byExchange } from "@/lib/actor";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { TabPanel, Tabs } from "@/components/ui/Tabs";
import { ConfirmReasonDialog } from "@/components/ui/ConfirmReasonDialog";
import { OrdersTable } from "@/components/OrdersTable";
import { PositionsTable } from "@/components/PositionsTable";
import { TradesTable } from "@/components/TradesTable";
import { LoginLogTable, OrderLogTable } from "@/components/ClientLogTables";
import { cn } from "@/lib/cn";
import {
  accountCommand,
  fetchAccount,
  fetchAccountEvents,
  fetchAccountLoginLog,
  fetchAccountOrderHistory,
  fetchAccountOrderLog,
  fetchAccountOrders,
  fetchAccountPositions,
  fetchAccountTrades,
  keys,
  type AccountCommand,
} from "@/lib/queries";
import { accountStatusTone, brokerStatusTone } from "@/lib/status";
import {
  ACTIVE_POLL_MS,
  ACTIVE_POSITION_STATUSES,
  DEFAULT_USER_TAB,
  USER_TABS,
  isUserTab,
  shortId,
  type UserTab,
} from "@/lib/trading";
import type { BrokerAccountItem } from "@/lib/types";
import type { TranslationKey } from "@/i18n/en";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

interface UserDetailSearch {
  /** The tab (absent = active orders). */
  tab?: UserTab;
}

export const Route = createFileRoute("/_app/users/$accountId")({
  validateSearch: (s: Record<string, unknown>): UserDetailSearch => ({
    tab: isUserTab(s.tab) ? s.tab : undefined,
  }),
  beforeLoad: capabilityGuard("VIEW"),
  component: UserDetailPage,
});

function UserDetailPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { accountId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { nodeId } = useScope();
  const probe = { queryKey: keys.account(nodeId, accountId), queryFn: () => fetchAccount(nodeId, accountId) };

  // Several account tabs open side by side stay apart (this route only).
  useEffect(() => {
    const before = document.title;
    document.title = `${shortId(accountId)} · ${t("users.title")}`;
    return () => {
      document.title = before;
    };
  }, [accountId, t]);

  return (
    <Guarded guard={guard} probe={probe}>
      <BackToUsers className="mb-2 inline-block" />
      <PageHeader
        title={t("users.detailTitle")}
        subtitle={<span className="mono">{accountId}</span>}
        coverage="none"
      />
      <AccountDetail
        accountId={accountId}
        tab={search.tab ?? DEFAULT_USER_TAB}
        // ?tab= survives a reload; the default stays out of the URL
        onTab={(tab) => void navigate({ search: (prev) => ({ ...prev, tab: tab === DEFAULT_USER_TAB ? undefined : tab }) })}
      />
    </Guarded>
  );
}

/** "← Users", with the same node and currency (for a tab opened directly). */
function BackToUsers({ className }: { className?: string }) {
  const { t } = useI18n();
  const shell = useShellSearch();
  return (
    <Link to="/users" search={shell} className={cn("text-xs font-semibold text-neon hover:underline", className)}>
      ← {t("users.title")}
    </Link>
  );
}

function AccountDetail({ accountId, tab, onTab }: { accountId: string; tab: UserTab; onTab: (tab: UserTab) => void }) {
  const { t } = useI18n();
  const scope = useScope();
  const canPause = useCan("ACCOUNT_PAUSE");
  const qc = useQueryClient();
  const [cmd, setCmd] = useState<AccountCommand | null>(null);
  const [eventsOffset, setEventsOffset] = useState(0);
  const accQ = useQuery({ queryKey: keys.account(scope.nodeId, accountId), queryFn: () => fetchAccount(scope.nodeId, accountId) });
  const evParams = { offset: eventsOffset, limit: 10 };
  const eventsQ = useQuery({
    queryKey: keys.accountEvents(scope.nodeId, accountId, evParams),
    queryFn: () => fetchAccountEvents(scope.nodeId, accountId, evParams),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["broker", scope.nodeId] });

  // An account outside the node's subtree (or a bad id) answers 404/403:
  // the error state with a way back, never an empty account.
  if (accQ.error) {
    return (
      <SectionCard title={t("users.accountCard")}>
        <QueryBlock query={accQ}>{() => null}</QueryBlock>
        <BackToUsers className="mt-3 inline-block" />
      </SectionCard>
    );
  }

  return (
    <SectionCard title={t("users.accountCard")}>
      <QueryBlock query={accQ}>
        {(a) => (
          <div className="space-y-4">
            <KeyValues
              rows={[
                { label: t("users.account"), value: <span className="mono text-xs">{a.broker_account_id}</span> },
                { label: t("users.user"), value: <span className="mono text-xs">{a.user_id}</span> },
                { label: t("users.servicingNode"), value: <span className="mono text-xs">{a.broker_id}</span> },
                { label: t("users.balanceMode"), value: t(`balanceMode.${a.balance_mode}`) },
                {
                  label: t("common.status"),
                  value: <Badge tone={accountStatusTone(a.status)}>{t(`accountStatus.${a.status}`)}</Badge>,
                },
                {
                  label: t("users.brokerEffective"),
                  value: a.effective_status ? (
                    <Badge tone={brokerStatusTone(a.effective_status)}>{t(`brokerStatus.${a.effective_status}`)}</Badge>
                  ) : (
                    "—"
                  ),
                },
                { label: t("users.entryPath"), value: t(`entryPath.${a.entry_path}`) },
                { label: t("users.statusReason"), value: a.status_reason || "—" },
                { label: t("users.created"), value: <Time ms={a.created_at_unix_ms} /> },
                { label: t("users.updated"), value: <Time ms={a.updated_at_unix_ms} /> },
              ]}
            />
            <PauseControls account={a} canPause={canPause} nodeId={scope.nodeId} onCommand={setCmd} />
            {cmd && (
              <ConfirmReasonDialog
                title={t(`users.cmd.${cmd}.title`, { id: a.broker_account_id })}
                description={t(`users.cmd.${cmd}.body`)}
                confirmLabel={t(`users.cmd.${cmd}.confirm`)}
                danger={cmd === "suspend"}
                onConfirm={async (reason) => {
                  const res = await accountCommand(scope.nodeId, a.broker_account_id, cmd, {
                    reason,
                    expected_row_version: a.row_version,
                  });
                  return { auditIds: res.auditIds };
                }}
                onClose={() => setCmd(null)}
                onDone={() => {
                  setCmd(null);
                  void refresh();
                }}
              />
            )}
          </div>
        )}
      </QueryBlock>

      <AccountTabs key={`${accountId}|${scope.currency}`} accountId={accountId} tab={tab} onTab={onTab} />

      <h3 className="section-title mb-2 mt-5">{t("users.eventsTitle")}</h3>
      <QueryBlock query={eventsQ} empty={(d) => ((d.items ?? []).length === 0 ? <EmptyState title={t("users.noEvents")} /> : null)}>
        {(d) => (
          <>
            <ul className="divide-y divide-line-1">
              {(d.items ?? []).map((e) => (
                <li key={e.event_id} className="py-2 text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-t-1">{labelOf(t, "accountEvent", e.action)}</span>
                    <Time ms={e.occurred_at_unix_ms} className="text-t-3" />
                  </div>
                  <p className="text-t-3">
                    {byExchange(e.actor_id, e.actor_kind)
                      ? t("users.eventActorExchange")
                      : t("users.eventActor", { kind: labelOf(t, "actorKind", e.actor_kind), id: e.actor_id })}
                  </p>
                  {e.reason && (
                    <p className="text-t-2">
                      {/* a CREATE event's reason is the entry path (broker-service accounts.go) */}
                      {t("users.eventReason", { reason: e.action === "CREATE" ? labelOf(t, "entryPath", e.reason) : e.reason })}
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <Pager page={d.page} onOffset={setEventsOffset} />
          </>
        )}
      </QueryBlock>
    </SectionCard>
  );
}

const TAB_LABEL: Record<UserTab, TranslationKey> = {
  orders: "users.tab.orders",
  positions: "users.tab.positions",
  history: "users.tab.history",
  trades: "users.tab.trades",
  "login-log": "users.loginLogTitle",
  "order-log": "users.orderLogTitle",
};

/** The tabs paged by offset (the order log pages by cursor). */
type OffsetTab = Exclude<UserTab, "order-log">;
const FIRST_PAGES: Record<OffsetTab, number> = { orders: 0, positions: 0, history: 0, trades: 0, "login-log": 0 };
const TAB_LIMIT = 10;

/**
 * The account's activity, one tab at a time (SM-828): its working orders,
 * active positions (OPEN and REDEEMABLE), finished orders and fills in the
 * selected currency, then SM-822's login log and order log. Only the open
 * tab reads: each panel holds its own query, QueryBlock, pager and empty
 * state, so a failing tab (an unavailable peer's 503 included) shows only
 * inside itself, never in the account panel or another tab. Active orders
 * and positions poll every 30 s; history, trades and the logs do not.
 * Every tab keeps its own page while another one is open. Keyed by account
 * and currency: another account, or another currency, starts every tab on
 * page one (an order-log cursor is never replayed against another
 * account). Read-only: nothing here cancels or closes.
 */
function AccountTabs({ accountId, tab, onTab }: { accountId: string; tab: UserTab; onTab: (tab: UserTab) => void }) {
  const { t } = useI18n();
  const { currency } = useScope();
  const idBase = useId();
  const [offsets, setOffsets] = useState(FIRST_PAGES);
  // the cursor each order-log page after the first was read with
  const [orderLogCursors, setOrderLogCursors] = useState<string[]>([]);
  const pageOf = (k: OffsetTab) => ({
    offset: offsets[k],
    onOffset: (o: number) => setOffsets((prev) => ({ ...prev, [k]: o })),
  });
  const trading = tab === "orders" || tab === "positions" || tab === "history" || tab === "trades";

  return (
    <div className="mt-5">
      <Tabs
        label={t("users.tabs")}
        idBase={idBase}
        tabs={USER_TABS.map((k) => ({ key: k, label: t(TAB_LABEL[k]) }))}
        value={tab}
        onChange={onTab}
        className="mb-3"
      />
      <TabPanel idBase={idBase} value={tab}>
        {trading && (
          <p className="mb-2 flex items-center gap-1 text-2xs text-t-3">
            {t("header.currency")} <CurrencyBadge currency={currency} />
          </p>
        )}
        {tab === "orders" && <AccountOrders accountId={accountId} {...pageOf("orders")} />}
        {tab === "positions" && <AccountPositions accountId={accountId} {...pageOf("positions")} />}
        {tab === "history" && <AccountOrderHistory accountId={accountId} {...pageOf("history")} />}
        {tab === "trades" && <AccountTrades accountId={accountId} {...pageOf("trades")} />}
        {tab === "login-log" && <AccountLoginLog accountId={accountId} {...pageOf("login-log")} />}
        {tab === "order-log" && <AccountOrderLog accountId={accountId} cursors={orderLogCursors} onCursors={setOrderLogCursors} />}
      </TabPanel>
    </div>
  );
}

interface TabPage {
  accountId: string;
  offset: number;
  onOffset: (offset: number) => void;
}

function AccountOrders({ accountId, offset, onOffset }: TabPage) {
  const { t } = useI18n();
  const { nodeId, currency } = useScope();
  const p = { currency, offset, limit: TAB_LIMIT };
  const q = useQuery({
    queryKey: keys.accountOrders(nodeId, accountId, p),
    queryFn: () => fetchAccountOrders(nodeId, accountId, p),
    refetchInterval: ACTIVE_POLL_MS,
  });
  return (
    <QueryBlock
      query={q}
      empty={(d) =>
        (d.orders ?? []).length === 0 ? (
          <EmptyPage title={t("orders.emptyActive")} hint={t("trading.emptyHintAccount")} page={d.page} onOffset={onOffset} />
        ) : null
      }
    >
      {(d) => (
        <>
          <OrdersTable orders={d.orders ?? []} variant="active" compact />
          <Pager page={d.page} onOffset={onOffset} />
        </>
      )}
    </QueryBlock>
  );
}

function AccountPositions({ accountId, offset, onOffset }: TabPage) {
  const { t } = useI18n();
  const { nodeId, currency } = useScope();
  const p = { currency, status: ACTIVE_POSITION_STATUSES, offset, limit: TAB_LIMIT };
  const q = useQuery({
    queryKey: keys.accountPositions(nodeId, accountId, p),
    queryFn: () => fetchAccountPositions(nodeId, accountId, p),
    refetchInterval: ACTIVE_POLL_MS,
  });
  return (
    <QueryBlock
      query={q}
      empty={(d) =>
        (d.positions ?? []).length === 0 ? (
          <EmptyPage title={t("positions.emptyActive")} hint={t("trading.emptyHintAccount")} page={d.page} onOffset={onOffset} />
        ) : null
      }
    >
      {(d) => (
        <>
          <PositionsTable positions={d.positions ?? []} compact />
          <Pager page={d.page} onOffset={onOffset} />
        </>
      )}
    </QueryBlock>
  );
}

function AccountOrderHistory({ accountId, offset, onOffset }: TabPage) {
  const { t } = useI18n();
  const { nodeId, currency } = useScope();
  const p = { currency, offset, limit: TAB_LIMIT };
  const q = useQuery({
    queryKey: keys.accountOrderHistory(nodeId, accountId, p),
    queryFn: () => fetchAccountOrderHistory(nodeId, accountId, p),
  });
  return (
    <QueryBlock
      query={q}
      empty={(d) =>
        (d.orders ?? []).length === 0 ? (
          <EmptyPage title={t("orders.emptyHistory")} hint={t("trading.emptyHintAccount")} page={d.page} onOffset={onOffset} />
        ) : null
      }
    >
      {(d) => (
        <>
          <OrdersTable orders={d.orders ?? []} variant="history" compact />
          <Pager page={d.page} onOffset={onOffset} />
        </>
      )}
    </QueryBlock>
  );
}

function AccountTrades({ accountId, offset, onOffset }: TabPage) {
  const { t } = useI18n();
  const { nodeId, currency } = useScope();
  const p = { currency, offset, limit: TAB_LIMIT };
  const q = useQuery({
    queryKey: keys.accountTrades(nodeId, accountId, p),
    queryFn: () => fetchAccountTrades(nodeId, accountId, p),
  });
  // RED holds a GAME user's buys only: their sells settle in GREEN.
  const hint =
    currency === "RED" ? (
      <>
        {t("trading.emptyHintAccount")} {t("trades.gameSellsNote")}
      </>
    ) : (
      t("trading.emptyHintAccount")
    );
  return (
    <QueryBlock
      query={q}
      empty={(d) =>
        (d.trades ?? []).length === 0 ? <EmptyPage title={t("trades.empty")} hint={hint} page={d.page} onOffset={onOffset} /> : null
      }
    >
      {(d) => (
        <>
          <TradesTable trades={d.trades ?? []} compact />
          <Pager page={d.page} onOffset={onOffset} />
        </>
      )}
    </QueryBlock>
  );
}

/** SM-822's login log, as a tab. */
function AccountLoginLog({ accountId, offset, onOffset }: TabPage) {
  const { t } = useI18n();
  const { nodeId } = useScope();
  const loginParams = { offset, limit: TAB_LIMIT };
  const loginQ = useQuery({
    queryKey: keys.accountLoginLog(nodeId, accountId, loginParams),
    queryFn: () => fetchAccountLoginLog(nodeId, accountId, loginParams),
  });
  return (
    <QueryBlock
      query={loginQ}
      empty={(d) =>
        (d.items ?? []).length === 0 ? <EmptyState title={t("users.loginLogEmpty")} hint={t("users.loginLogHint")} /> : null
      }
    >
      {(d) => (
        <>
          <LoginLogTable items={d.items ?? []} />
          <Pager page={d.page} onOffset={onOffset} />
        </>
      )}
    </QueryBlock>
  );
}

/** SM-822's order log (where each order was placed from), as a tab. Keyset-paged. */
function AccountOrderLog({
  accountId,
  cursors,
  onCursors,
}: {
  accountId: string;
  cursors: string[];
  onCursors: (cursors: string[]) => void;
}) {
  const { t } = useI18n();
  const { nodeId } = useScope();
  const orderLogParams = { cursor: cursors[cursors.length - 1], limit: TAB_LIMIT };
  const orderLogQ = useQuery({
    queryKey: keys.accountOrderLog(nodeId, accountId, orderLogParams),
    queryFn: () => fetchAccountOrderLog(nodeId, accountId, orderLogParams),
  });
  // the cursor pager sits outside the QueryBlock, so a later page that
  // fails or comes back empty can still be left with Previous
  const orderLogRows = orderLogQ.data?.items ?? [];

  return (
    <>
      <QueryBlock
        query={orderLogQ}
        empty={(d) =>
          (d.items ?? []).length === 0 ? <EmptyState title={t("users.orderLogEmpty")} hint={t("users.orderLogHint")} /> : null
        }
      >
        {(d) => (
          <>
            <OrderLogTable items={d.items ?? []} />
            {(d.items ?? []).some((o) => !o.channel) && (
              <p className="mt-2 text-2xs text-t-3">{t("users.orderLogUnrecordedNote")}</p>
            )}
          </>
        )}
      </QueryBlock>
      {(orderLogRows.length > 0 || cursors.length > 0) && (
        <CursorPager cursors={cursors} nextCursor={orderLogQ.data?.next_cursor} onCursors={onCursors} />
      )}
    </>
  );
}

function PauseControls({
  account,
  canPause,
  nodeId,
  onCommand,
}: {
  account: BrokerAccountItem;
  canPause: boolean;
  nodeId: string;
  onCommand: (c: AccountCommand) => void;
}) {
  const { t } = useI18n();
  if (account.status === "CLOSED") return <p className="text-xs text-t-3">{t("users.closedNote")}</p>;
  if (!canPause) return <p className="text-xs text-t-3">{t("users.pauseNeedsRole")}</p>;
  if (account.broker_id !== nodeId) return <p className="text-xs text-t-3">{t("users.pauseDirectOnly")}</p>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {account.status === "ACTIVE" && (
        <Button variant="danger" size="sm" onClick={() => onCommand("suspend")}>
          {t("users.cmd.suspend.confirm")}
        </Button>
      )}
      {account.status === "SUSPENDED" && (
        <Button size="sm" onClick={() => onCommand("resume")}>
          {t("users.cmd.resume.confirm")}
        </Button>
      )}
      <p className="text-2xs text-t-3">{t("users.pauseNote")}</p>
    </div>
  );
}
